import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, NgZone, OnDestroy, Output, PLATFORM_ID, ViewChild } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  reset(widgetId?: string): void;
  remove(widgetId: string): void;
}
declare global { interface Window { turnstile?: TurnstileApi; } }

@Component({
  selector: 'app-turnstile',
  standalone: true,
  imports: [CommonModule],
  template: `<div #container></div><p class="error" *ngIf="error">{{ error }}</p>`,
  styles: [`:host{display:block;margin:16px 0}.error{margin:8px 0 0;color:#b42318;font-size:14px}`],
})
export class TurnstileComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) action = '';
  @Output() tokenChange = new EventEmitter<string>();
  @ViewChild('container', { static: true }) container!: ElementRef<HTMLElement>;
  error = '';
  private widgetId?: string;
  private static scriptPromise?: Promise<void>;

  constructor(@Inject(PLATFORM_ID) private platformId: object, private http: HttpClient, private zone: NgZone) {}

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const [{ siteKey }] = await Promise.all([
        firstValueFrom(this.http.get<{ siteKey: string }>('/api/turnstile/config')),
        this.loadScript(),
      ]);
      if (!window.turnstile) throw new Error('Turnstile non disponibile');
      this.widgetId = window.turnstile.render(this.container.nativeElement, {
        sitekey: siteKey,
        action: this.action,
        theme: 'auto',
        appearance: 'interaction-only',
        'response-field': false,
        callback: (token: string) => this.zone.run(() => { this.error = ''; this.tokenChange.emit(token); }),
        'expired-callback': () => this.zone.run(() => this.tokenChange.emit('')),
        'error-callback': () => this.zone.run(() => {
          this.error = 'Verifica di sicurezza non disponibile. Ricarica la pagina.';
          this.tokenChange.emit('');
        }),
      });
    } catch (error) {
      console.error('Errore inizializzazione Turnstile:', error);
      this.error = 'Verifica di sicurezza non disponibile. Ricarica la pagina.';
    }
  }

  reset(): void {
    this.tokenChange.emit('');
    if (this.widgetId && window.turnstile) window.turnstile.reset(this.widgetId);
  }
  ngOnDestroy(): void {
    if (this.widgetId && isPlatformBrowser(this.platformId) && window.turnstile) window.turnstile.remove(this.widgetId);
  }
  private loadScript(): Promise<void> {
    if (window.turnstile) return Promise.resolve();
    if (TurnstileComponent.scriptPromise) return TurnstileComponent.scriptPromise;
    TurnstileComponent.scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Caricamento Turnstile fallito'));
      document.head.appendChild(script);
    });
    return TurnstileComponent.scriptPromise;
  }
}
